import {
  AccessibleDatabase,
  CreateDatabasePayloadV2,
  CreateDatabaseResponse,
  CreateServerlessDatabasePayload,
  CreateServerlessResponse,
  DatabaseCosts,
  DatabaseListResponse,
  DatabaseNodeListResponse,
  DataBaseProtectionPayload,
  DatabaseResponse,
  DatabaseSchedulePayload,
  DatabaseScheduleResponse,
  DeployTrialDatabasePayload,
  JobResponse,
  PreAuthPayload,
  TargetClustersListResponse,
  TogglePlaygroundPayload,
  TurnOnAppServicePayload,
  UpdateDatabasePayload,
  UpdateDatabasePlanPayload,
} from '@couchbasecloud/base-ui/types/database-service.types';
import { defaultPaginationQuery, QueryOptions } from 'sync/query';
import { deleteRequest, getRequest, postRequest, putRequest } from 'sync/request';
import { DatabaseContext, OrganizationContext, ProjectContext } from 'sync/request.types';

export const listDatabases = (params: OrganizationContext, query: QueryOptions = defaultPaginationQuery) => {
  return getRequest<DatabaseListResponse>(`/v2/organizations/${params.organizationId}/clusters`, {
    params: query,
  });
};

/**
 * Request to return a list of nodes for a specified `organization` database.
 */
export const listDatabaseNodes = (params: DatabaseContext) => {
  return getRequest<DatabaseNodeListResponse>(
    `/v2/organizations/${params.organizationId}/projects/${params.projectId}/clusters/${params.databaseId}/nodes`
  );
};

/**
 * **Queryable** request to return a list of databases against a specified project.
 */
export const listProjectDatabases = (params: ProjectContext, query: QueryOptions = defaultPaginationQuery) => {
  return getRequest<DatabaseListResponse>(`/v2/organizations/${params.organizationId}/projects/${params.projectId}/clusters`, {
    params: query,
  });
};

/**
 * Request to return a specified `organization` database.
 *
 * TODO: This endpoint needs to be updated at the API layer to return a single organization and not a list of organizations.
 */
export const getDatabase = (params: DatabaseContext) => {
  return getRequest<DatabaseResponse>(
    `/v2/organizations/${params.organizationId}/projects/${params.projectId}/clusters/${params.databaseId}`
  );
};

/**
 * Return a list of accessible databases.
 */
export const getAccessibleDatabases = (params: OrganizationContext) => {
  return getRequest<AccessibleDatabase[]>(`/v2/organizations/${params.organizationId}/clusters/accessible-clusters`);
};

/**
 * Request to update a **provisioned** database details.
 */
export const updateDatabase = (params: DatabaseContext, data: UpdateDatabasePayload) => {
  return postRequest<{}>(`/v2/organizations/${params.organizationId}/projects/${params.projectId}/clusters/${params.databaseId}/meta`, {
    data,
  });
};

/**
 * Request to update a **provisioned** database plan.
 */
export const updateDatabasePlan = (params: DatabaseContext, data: UpdateDatabasePlanPayload) => {
  return postRequest<{}>(`/v2/organizations/${params.organizationId}/projects/${params.projectId}/clusters/${params.databaseId}/package`, {
    data,
  });
};

/**
 * Request to delete a specified **provisioned* database.
 */
export const deleteDatabase = (params: DatabaseContext, deleteBackups: boolean = false) => {
  return deleteRequest<DatabaseResponse>(
    `/v2/organizations/${params.organizationId}/projects/${params.projectId}/clusters/${params.databaseId}?retainSnapshotBackups=${deleteBackups}`
  );
};

/**
 * Request to delete a specified **provisioned* database.
 */
export const deleteServerlessDatabase = (params: DatabaseContext, deleteBackups: boolean = false) => {
  return deleteRequest<DatabaseResponse>(
    `/v2/organizations/${params.organizationId}/projects/${params.projectId}/databases/${params.databaseId}?retainSnapshotBackups=${deleteBackups}`
  );
};

/**
 * Request to deploy a **provisioned** database.
 */
export const createDatabaseV2 = (params: OrganizationContext, data: CreateDatabasePayloadV2) => {
  return postRequest<CreateDatabaseResponse>(`/v2/organizations/${params.organizationId}/clusters/v2`, {
    data,
  });
};

/**
 * Request to deploy a **provisioned trial** database.
 */
export const createTrialDatabase = (params: OrganizationContext, data: DeployTrialDatabasePayload) => {
  return postRequest<{}>(`/v2/organizations/${params.organizationId}/trial/cluster`, { data });
};

/**
 * Request to deploy a **serverless** database.
 */
export const createServerlessDatabase = (params: OrganizationContext, data: CreateServerlessDatabasePayload) => {
  return postRequest<CreateServerlessResponse>(`/v2/organizations/${params.organizationId}/databases`, {
    data,
  });
};

export const getDataJobs = (params: DatabaseContext) => {
  return getRequest<JobResponse>(
    `/v2/organizations/${params.organizationId}/projects/${params.projectId}/clusters/${params.databaseId}/jobs`
  );
};

/**
 * Request to enable/disable playground for a database.
 */
export const toggleDatabasePlayground = (params: DatabaseContext, data: TogglePlaygroundPayload) => {
  return putRequest<{}>(
    `/v2/organizations/${params.organizationId}/projects/${params.projectId}/clusters/${params.databaseId}/playground`,
    { data }
  );
};

export const updatePreAuth = (params: DatabaseContext, payload: PreAuthPayload) => {
  return putRequest<void>(
    `/v2/organizations/${params.organizationId}/projects/${params.projectId}/clusters/${params.databaseId}/pre-authorize-support`,
    { data: payload }
  );
};

export const toggleDatabaseDeletionProtection = (params: DatabaseContext, data: DataBaseProtectionPayload) => {
  return putRequest<{}>(
    `/v2/organizations/${params.organizationId}/projects/${params.projectId}/clusters/${params.databaseId}/deletion-protection`,
    {
      data,
    }
  );
};

/**
 * Request to enable/disable playground for a database.
 */
export const turnDatabaseOff = (params: DatabaseContext) => {
  return postRequest<{}>(`/v2/organizations/${params.organizationId}/projects/${params.projectId}/clusters/${params.databaseId}/off`);
};

/**
 * Request to enable/disable playground for a database.
 */
export const turnDatabaseOn = (params: DatabaseContext, data?: TurnOnAppServicePayload) => {
  return postRequest<{}>(`/v2/organizations/${params.organizationId}/projects/${params.projectId}/clusters/${params.databaseId}/on`, {
    data,
  });
};

/**
 * Request to enable/disable playground for a database.
 */
export const resumeDatabase = (params: DatabaseContext) => {
  return postRequest<{}>(`/v2/organizations/${params.organizationId}/projects/${params.projectId}/clusters/${params.databaseId}/resume`);
};

/**
 *  Request to return costs of turning off the database for a specified organization.
 */
export const getDatabaseCosts = (params: DatabaseContext) => {
  return getRequest<DatabaseCosts>(
    `/v2/organizations/${params.organizationId}/projects/${params.projectId}/clusters/${params.databaseId}/fixed-costs`
  );
};

export function eligibleLmClustersList(
  { organizationId }: { organizationId: string },
  params: { type?: string } & QueryOptions = defaultPaginationQuery
) {
  return getRequest<DatabaseListResponse>(`/v2/organizations/${organizationId}/lmservices/eligibility`, { params });
}
/**
 * Request to get database schedule on/off
 */

export const getDatabaseSchedule = (params: DatabaseContext) => {
  return getRequest<DatabaseScheduleResponse>(
    `/v2/organizations/${params.organizationId}/projects/${params.projectId}/clusters/${params.databaseId}/schedules/onoff`
  );
};

/**
 * Request to set database schedule on/off
 */

export const setDatabaseSchedule = (params: DatabaseContext, data: DatabaseSchedulePayload) => {
  return postRequest<{}>(
    `/v2/organizations/${params.organizationId}/projects/${params.projectId}/clusters/${params.databaseId}/schedules/onoff`,
    {
      data,
    }
  );
};

/*
 * Request to delete database schedule on/off
 */

export const deleteDatabaseSchedule = (params: DatabaseContext) => {
  return deleteRequest<DatabaseResponse>(
    `/v2/organizations/${params.organizationId}/projects/${params.projectId}/clusters/${params.databaseId}/schedules/onoff`
  );
};

/*
 * Request to turn on database schedule
 */

export const turnOffDatabaseSchedule = (params: DatabaseContext) => {
  return putRequest<{}>(
    `/v2/organizations/${params.organizationId}/projects/${params.projectId}/clusters/${params.databaseId}/schedules/onoff/pause`
  );
};

/*
 * Request to turn off database schedule
 */
export const turnOnDatabaseSchedule = (params: DatabaseContext) => {
  return putRequest<{}>(
    `/v2/organizations/${params.organizationId}/projects/${params.projectId}/clusters/${params.databaseId}/schedules/onoff/unpause`
  );
};

export const restoreTargetClusters = (
  params: {
    orgId: string;
    projectId: string;
    backupId: string;
  },
  query: QueryOptions = defaultPaginationQuery
) => {
  return getRequest<TargetClustersListResponse>(
    `/v2/organizations/${params.orgId}/projects/${params.projectId}/cloudsnapshotbackups/${params.backupId}/targetclusters`,
    {
      params: query,
    }
  );
};
